import { Link } from "@tanstack/react-router"
import {
  ArrowUpRight,
  AudioLines,
  ChevronLeft,
  ChevronRight,
  MessageSquare,
  Waypoints,
} from "lucide-react"
import { motion } from "motion/react"
import { useEffect, useRef, useState, type ReactNode } from "react"

import { STAGE_SPRING } from "@/components/coach-stage"
import type { Recommendation } from "@/components/recommendation-tiles"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { useProduktSperre } from "@/lib/produkt-freigabe"
import type { RecentEntry } from "@/lib/use-recent-entries"
import { cn } from "@/lib/utils"
import {
  KUNDEN_PERSPEKTIVEN,
  TRAINING_ANLAESSE,
  TRAINING_PRODUKTE,
  type CoachStyle,
  type ProduktKey,
} from "@nl/shared"

/** What every start page draft gets from the route. */
export type StartLayoutProps = {
  coach: CoachStyle | null
  coachName: string
  firstName?: string
  standHint?: string
  composer: ReactNode
  onSpeak: () => void
  voice?: ReactNode | null
  recommendations: Recommendation[]
  entries: RecentEntry[]
  entriesLoading: boolean
  /** The training the advisor can pick up, if one is running. */
  resume?: { to: string; title: string; line: string } | null
  completedCount: number
}

export type TileShape = "wide" | "poster" | "large"
export type ImageSet = "fotos" | "abstrakt" | "menschen"

/**
 * The drafts have their own pictures, one motif per module. People in
 * everyday situations are the default, the way neueleben.de illustrates; the
 * landscape and abstract sets stay selectable. The module lists keep pointing
 * at the old pictures, so the path is rewritten here instead of touching the
 * domain until a direction is chosen.
 */
export function tileImage(
  imageUrl: string,
  shape: TileShape,
  set: ImageSet = "menschen"
): string {
  const folder =
    set === "fotos" ? (shape === "poster" ? "fotos/poster" : "fotos") : set
  return imageUrl.replace(/^\/(themen|produkte)\//, `/${folder}/`)
}

export type TileRowData = { title: string; items: Recommendation[] }

export function useModuleRows(recommendations: Recommendation[]): TileRowData[] {
  return moduleRows(recommendations, useProduktSperre())
}

function moduleRows(
  recommendations: Recommendation[],
  produktSperre: (produkt: ProduktKey) => string | undefined
): TileRowData[] {
  return [
    { title: "Deine nächsten Schritte", items: recommendations },
    {
      title: "Gesprächstrainings",
      items: TRAINING_ANLAESSE.map((t) => ({
        key: `thema-${t.key}`,
        kind: "Gesprächstraining",
        title: t.shortLabel,
        line: "",
        imageUrl: t.imageUrl,
        to: "/durchlauf",
        search: { schritt: "thema" },
        available: t.available,
      })),
    },
    {
      title: "Produkttrainings",
      items: TRAINING_PRODUKTE.map((p) => ({
        key: `produkt-${p.key}`,
        kind: "Produkttraining",
        title: p.label,
        line: "",
        imageUrl: p.imageUrl,
        to: "/produkttraining",
        search: { produkt: p.key },
        available: p.available && !produktSperre(p.key),
        sperre: p.available ? produktSperre(p.key) : undefined,
      })),
    },
  ]
}

export function ModuleTile({
  item,
  shape,
  className,
  set,
}: {
  item: Recommendation
  shape: TileShape
  className?: string
  set?: ImageSet
}) {
  const aspect =
    shape === "poster"
      ? "aspect-[2/3]"
      : shape === "large"
        ? "aspect-[16/9]"
        : "aspect-[16/10]"
  const frame = cn(
    "group relative block shrink-0 overflow-hidden rounded-xl bg-muted ring-1 ring-foreground/10 outline-none",
    aspect,
    item.available
      ? "motion-safe:transition-shadow motion-safe:duration-300 hover:shadow-[0_12px_32px_rgba(31,31,31,0.16)] focus-visible:ring-2 focus-visible:ring-ring/60"
      : "cursor-default",
    className
  )
  const surface = (
    <>
      <img
        src={tileImage(item.imageUrl, shape, set)}
        alt=""
        loading="lazy"
        className={cn(
          "absolute inset-0 h-full w-full object-cover",
          "motion-safe:transition-transform motion-safe:duration-700 motion-safe:ease-out motion-safe:group-hover:scale-[1.04]",
          "dark:brightness-90",
          !item.available && "saturate-[.75]"
        )}
      />
      <div
        aria-hidden
        className="absolute inset-0 bg-[linear-gradient(to_top,rgba(0,0,0,0.88)_0%,rgba(0,0,0,0.55)_38%,rgba(0,0,0,0.12)_70%,transparent_100%)]"
      />
      {!item.available ? (
        <span className="absolute top-3 right-3 rounded-full bg-white/85 px-2.5 py-1 text-[11px] font-bold tracking-[0.04em] uppercase text-[var(--nl-ink)]/70">
          {item.sperre ?? "Bald"}
        </span>
      ) : null}
      <div
        className={cn(
          "absolute inset-x-0 bottom-0 text-white [text-shadow:0_1px_2px_rgba(0,0,0,0.45)]",
          shape === "poster" ? "p-4" : "p-5"
        )}
      >
        <div className="text-[11px] font-bold tracking-[0.06em] uppercase text-white/70">
          {item.kind}
        </div>
        <div
          className={cn(
            "mt-1 font-display leading-snug font-medium tracking-tight",
            shape === "poster" ? "text-[16px]" : "text-[18px]"
          )}
        >
          {item.title}
        </div>
        {shape !== "poster" && item.line ? (
          <div className="mt-1 line-clamp-2 text-[13px] leading-snug text-white/85">
            {item.line}
          </div>
        ) : null}
      </div>
      {item.available ? (
        <ArrowUpRight className="absolute top-4 right-4 h-4 w-4 text-white/80 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100" />
      ) : null}
    </>
  )
  if (!item.available || !item.to) {
    return (
      <div className={frame} aria-disabled>
        {surface}
      </div>
    )
  }
  return (
    <Link to={item.to} search={item.search} className={frame}>
      {surface}
    </Link>
  )
}

/**
 * A shelf the way a streaming catalogue shows one: title, then a row that
 * scrolls sideways. Where more is hidden, the edge fades into the page and an
 * arrow says so; both go as soon as the end is reached.
 */
export function TileRow({
  row,
  shape,
  tileWidth,
  set,
}: {
  row: TileRowData
  shape: TileShape
  tileWidth: string
  set?: ImageSet
}) {
  const scroller = useRef<HTMLDivElement>(null)
  const [edges, setEdges] = useState({ left: false, right: false })

  useEffect(() => {
    const el = scroller.current
    if (!el) return
    const update = () => {
      const max = el.scrollWidth - el.clientWidth
      setEdges({ left: el.scrollLeft > 4, right: el.scrollLeft < max - 4 })
    }
    update()
    el.addEventListener("scroll", update, { passive: true })
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => {
      el.removeEventListener("scroll", update)
      ro.disconnect()
    }
  }, [row.items.length])

  const page = (dir: 1 | -1) => {
    const el = scroller.current
    if (!el) return
    el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: "smooth" })
  }

  return (
    <section className="min-w-0">
      <h2 className="mb-3 font-display text-[18px] font-medium tracking-tight">
        {row.title}
      </h2>
      <div className="relative">
        <div
          ref={scroller}
          className="flex gap-4 overflow-x-auto pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {row.items.map((item) => (
            <ModuleTile
              key={item.key}
              item={item}
              shape={shape}
              className={tileWidth}
              set={set}
            />
          ))}
        </div>
        <ShelfEdge side="left" visible={edges.left} onClick={() => page(-1)} />
        <ShelfEdge side="right" visible={edges.right} onClick={() => page(1)} />
      </div>
    </section>
  )
}

function ShelfEdge({
  side,
  visible,
  onClick,
}: {
  side: "left" | "right"
  visible: boolean
  onClick: () => void
}) {
  const Icon = side === "left" ? ChevronLeft : ChevronRight
  return (
    <div
      aria-hidden={!visible}
      className={cn(
        "absolute inset-y-0 flex w-12 items-center pb-2 transition-opacity duration-300",
        side === "left"
          ? "left-0 justify-start bg-gradient-to-r from-[var(--paper)]/80 to-transparent"
          : "right-0 justify-end bg-gradient-to-l from-[var(--paper)]/80 to-transparent",
        visible ? "opacity-100" : "pointer-events-none opacity-0"
      )}
    >
      <button
        type="button"
        onClick={onClick}
        tabIndex={visible ? 0 : -1}
        aria-label={side === "left" ? "Zurück" : "Weiter"}
        className="grid size-10 place-items-center rounded-full bg-[var(--paper)] text-foreground shadow-[0_8px_24px_rgba(31,31,31,0.22)] ring-1 ring-[var(--rule)] transition-transform hover:scale-105"
      >
        <Icon className="h-5 w-5" />
      </button>
    </div>
  )
}

export function TileGrid({
  row,
  shape,
  cols,
}: {
  row: TileRowData
  shape: TileShape
  cols: 2 | 3
}) {
  return (
    <section>
      <h2 className="mb-3 font-display text-[18px] font-medium tracking-tight">
        {row.title}
      </h2>
      <div
        className={cn(
          "grid gap-4",
          cols === 3 ? "sm:grid-cols-2 lg:grid-cols-3" : "sm:grid-cols-2"
        )}
      >
        {row.items.map((item) => (
          <ModuleTile key={item.key} item={item} shape={shape} />
        ))}
      </div>
    </section>
  )
}

export function SpeakButton({
  coachName,
  onSpeak,
  tone,
  className,
}: {
  coachName: string
  onSpeak: () => void
  tone: "outline" | "filled" | "inverse"
  className?: string
}) {
  return (
    <Button
      type="button"
      variant={tone === "outline" ? "outline" : "default"}
      onClick={onSpeak}
      className={cn(
        "h-auto min-h-11 shrink-0 gap-2 self-stretch px-4 text-[14px]",
        tone === "outline" && "bg-[var(--paper)]/85",
        tone === "inverse" &&
          "bg-white text-[var(--nl-red)] hover:bg-white/90 hover:text-[var(--nl-red-dark)]",
        className
      )}
    >
      <AudioLines
        className={cn(
          "h-4 w-4",
          tone === "outline" ? "text-primary" : "text-current"
        )}
      />
      Mit {coachName} sprechen
    </Button>
  )
}

/**
 * The prompt is sent verbatim as the first message, so it has to read as a
 * request; the label is what fits on a chip. Colours are the CI accent set.
 */
const SUGGESTION_CHIPS = [
  {
    label: "Einkommensgutachten üben",
    prompt: "Lass uns das Einkommensgutachten üben.",
    text: "text-[var(--nl-red-dark)] dark:text-[var(--nl-red-soft)]",
    bg: "bg-[color-mix(in_oklab,var(--nl-red)_16%,transparent)] hover:bg-[color-mix(in_oklab,var(--nl-red)_26%,transparent)]",
  },
  {
    label: "Aktivplan in Kundensprache",
    prompt: "Erklär mir den Aktivplan in Kundensprache.",
    text: "text-[var(--nl-blue-dark)] dark:text-[var(--nl-blue-soft)]",
    bg: "bg-[color-mix(in_oklab,var(--nl-blue)_16%,transparent)] hover:bg-[color-mix(in_oklab,var(--nl-blue)_26%,transparent)]",
  },
  {
    label: "Einwand zu den Garantien",
    prompt: "Gib mir einen Kundeneinwand zu den Garantien.",
    text: "text-[var(--nl-red-dark)] dark:text-[var(--nl-red-soft)]",
    bg: "bg-[color-mix(in_oklab,var(--nl-red-dark)_16%,transparent)] hover:bg-[color-mix(in_oklab,var(--nl-red-dark)_26%,transparent)]",
  },
  {
    label: "Was als Nächstes?",
    prompt: "Was sollte ich als Nächstes trainieren?",
    text: "text-[var(--nl-anthracite)] dark:text-[var(--nl-grey-light)]",
    bg: "bg-[color-mix(in_oklab,var(--nl-anthracite)_14%,transparent)] hover:bg-[color-mix(in_oklab,var(--nl-anthracite)_24%,transparent)]",
  },
]

/**
 * Opening suggestions as coloured chips. `onPhoto` puts a light pane behind
 * each chip so the tint still reads on a picture.
 */
export function SuggestionChips({
  limit,
  onPhoto,
  className,
}: {
  limit?: number
  onPhoto?: boolean
  className?: string
}) {
  return (
    <div className={cn("flex flex-wrap gap-2", className)}>
      {SUGGESTION_CHIPS.slice(0, limit).map((chip) => (
        <Link
          key={chip.label}
          to="/"
          search={{ frage: chip.prompt }}
          className={cn(
            "rounded-full px-3.5 py-1.5 text-[13px] font-medium transition-colors [text-shadow:none]",
            chip.text,
            onPhoto
              ? "bg-[var(--paper)]/92 shadow-[0_8px_24px_rgba(0,0,0,0.18)] backdrop-blur-xl hover:bg-[var(--paper)]"
              : chip.bg
          )}
        >
          {chip.label}
        </Link>
      ))}
    </div>
  )
}

/** The input, wrapped so the shared `layoutId` still carries it to the conversation. */
export function ComposerSlot({ children }: { children: ReactNode }) {
  return (
    <motion.div layoutId="composer" transition={STAGE_SPRING} className="min-w-0 flex-1">
      {children}
    </motion.div>
  )
}

/**
 * Mock until real progress exists. The customer wants a nudge, not a
 * scoreboard: one weekly goal and a direction per customer perspective,
 * nothing more to read off.
 */
const MOCK_WEEK = { done: 2, goal: 3 }
const MOCK_DIRECTION: Record<string, { hint: string; up: boolean }> = {
  fachlich: { hint: "sitzt", up: true },
  kundenzentriert: { hint: "im Aufbau", up: true },
  vertrieblich: { hint: "als Nächstes", up: false },
}

function WeekRing({ accent }: { accent: "red" | "blue" }) {
  const r = 26
  const c = 2 * Math.PI * r
  const share = MOCK_WEEK.done / MOCK_WEEK.goal
  return (
    <div className="flex items-center gap-4">
      <svg width="64" height="64" viewBox="0 0 64 64" aria-hidden className="shrink-0 -rotate-90">
        <circle cx="32" cy="32" r={r} fill="none" stroke="var(--rule)" strokeWidth="6" />
        <circle
          cx="32"
          cy="32"
          r={r}
          fill="none"
          stroke={accent === "blue" ? "var(--nl-blue)" : "var(--nl-red)"}
          strokeWidth="6"
          strokeLinecap="round"
          strokeDasharray={`${c * share} ${c}`}
        />
      </svg>
      <div>
        <div className="text-[14.5px] font-medium">
          {MOCK_WEEK.done} von {MOCK_WEEK.goal} Trainings
        </div>
        <div className="mt-0.5 text-[12.5px] text-muted-foreground">
          Dein Ziel für diese Woche
        </div>
      </div>
    </div>
  )
}

function Direction() {
  return (
    <ul className="space-y-2">
      {KUNDEN_PERSPEKTIVEN.map((k) => {
        const d = MOCK_DIRECTION[k.key]
        return (
          <li key={k.key} className="flex items-center justify-between gap-3 text-[13px]">
            <span>{k.label}</span>
            <span
              className={cn(
                "flex items-center gap-1.5 text-[12.5px]",
                d.up ? "text-emerald-600" : "text-muted-foreground"
              )}
            >
              {d.hint}
              <span aria-hidden>{d.up ? "↑" : "→"}</span>
            </span>
          </li>
        )
      })}
    </ul>
  )
}

export function ProgressPanel({
  resume,
  completedCount,
  accent,
  compact,
  flat,
}: {
  resume?: StartLayoutProps["resume"]
  completedCount: number
  accent: "red" | "blue"
  /** One horizontal strip instead of a column. */
  compact?: boolean
  /** A plain grey pane with straight edges instead of a card. */
  flat?: boolean
}) {
  const pane = flat
    ? "rounded-none border-0 bg-[var(--surface-alt)] shadow-none ring-0"
    : "border-[var(--rule)]"
  const resumeBlock = resume ? (
    <Link
      to={resume.to}
      className="group block rounded-lg bg-[var(--surface-alt)] px-4 py-3 transition-colors hover:bg-accent"
    >
      <div className="eyebrow">Weiter mit</div>
      <div className="mt-1 flex items-center gap-2 text-[14.5px] font-medium">
        <span className="truncate">{resume.title}</span>
        <ArrowUpRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground group-hover:text-foreground" />
      </div>
      <div className="mt-0.5 text-[12.5px] text-muted-foreground">{resume.line}</div>
    </Link>
  ) : null

  const count = (
    <div>
      <div className="font-display text-[30px] leading-none font-medium tracking-tight">
        {completedCount}
      </div>
      <div className="mt-1 text-[12.5px] text-muted-foreground">
        {completedCount === 1 ? "Training abgeschlossen" : "Trainings abgeschlossen"}
      </div>
    </div>
  )

  if (compact) {
    return (
      <Card className={cn("flex flex-col gap-6 px-5 pt-10 pb-5 md:flex-row md:items-center", pane)}>
        {resumeBlock ? <div className="min-w-[240px]">{resumeBlock}</div> : null}
        <WeekRing accent={accent} />
        <div className="hidden h-12 w-px bg-[var(--rule)] md:block" />
        {count}
        <div className="hidden h-12 w-px bg-[var(--rule)] md:block" />
        <div className="min-w-[220px] flex-1">
          <div className="eyebrow mb-2">Deine Richtung</div>
          <Direction />
        </div>
      </Card>
    )
  }

  return (
    <Card className={cn("gap-6 p-5", pane)}>
      <div>
        <div className="eyebrow mb-3">Diese Woche</div>
        <WeekRing accent={accent} />
      </div>
      {resumeBlock}
      {count}
      <div>
        <div className="eyebrow mb-3">Deine Richtung</div>
        <Direction />
      </div>
    </Card>
  )
}

export function RecentList({
  entries,
  isLoading,
  coachName,
  bare,
  flat,
}: {
  entries: RecentEntry[]
  isLoading: boolean
  coachName: string
  /** Without the card frame, for sitting inside another card. */
  bare?: boolean
  /** A plain grey pane with straight edges instead of a card. */
  flat?: boolean
}) {
  const rows = isLoading ? (
    <p className="px-5 py-4 text-[14px] text-muted-foreground">Wird geladen …</p>
  ) : entries.length === 0 ? (
    <p className="px-5 py-4 text-[14px] text-muted-foreground">
      Noch nichts vorhanden. Schreib {coachName} oder starte ein Training.
    </p>
  ) : (
    entries.map((entry) => (
      <Link
        key={entry.key}
        to={entry.to}
        search={entry.search}
        className="flex items-center gap-4 px-5 py-3.5 transition-colors hover:bg-muted/30"
      >
        {entry.kind === "chat" ? (
          <MessageSquare className="h-4 w-4 shrink-0 text-muted-foreground" />
        ) : (
          <Waypoints className="h-4 w-4 shrink-0 text-muted-foreground" />
        )}
        <div className="min-w-0 flex-1">
          <div className="truncate text-[14.5px] font-medium">{entry.title}</div>
          <div className="mt-0.5 truncate text-[12.5px] text-muted-foreground">
            {entry.subtitle}
          </div>
        </div>
        <ArrowUpRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      </Link>
    ))
  )
  return (
    <section>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="font-display text-[18px] font-medium tracking-tight">
          Zuletzt bearbeitet
        </h2>
        <Link
          to="/history"
          className="text-[13.5px] text-muted-foreground hover:text-foreground"
        >
          Alle ansehen →
        </Link>
      </div>
      {bare ? (
        <div className="divide-y divide-[var(--rule)]">{rows}</div>
      ) : (
        <Card
          className={cn(
            "gap-0 divide-y divide-[var(--rule)] p-0",
            flat
              ? "rounded-none border-0 bg-[var(--surface-alt)] shadow-none ring-0 divide-[var(--paper)]"
              : "border-[var(--rule)]"
          )}
        >
          {rows}
        </Card>
      )}
    </section>
  )
}
